import { classNames } from "primereact/utils";
import {
  SupportChatCategory,
  SupportChatPriority,
  SupportChatStatus,
} from "../../../common/enums";
import {
  ISupportChatListItem,
  supportChatCategoryLabels,
  supportChatPriorityLabels,
} from "../models/supportChatModels";
import styles from "./SupportChatItem.module.scss";
import { dateUtility } from "../../../common/infrastructure/utilities/dateUtility";

interface ISupportChatItemProps {
  className?: string;
  item?: ISupportChatListItem;
  onClick?: () => void;
}

export const SupportChatItem = ({
  className,
  item,
  onClick,
}: ISupportChatItemProps) => {
  if (!item)
    return (
      <div
        className={classNames(styles.root, styles.root_skeleton, className)}
      />
    );

  return (
    <div className={classNames(styles.root, className)} onClick={onClick}>
      <div className={styles.root_main}>
        <div className={styles.root_main_title}>
          <span className={styles.root_main_title_text}>{item.title}</span>
          {item.hasUnread && (
            <span className={styles.root_main_unread_dot} role="img" aria-label="Непрочетено" />
          )}
        </div>
        <div className={styles.root_main_meta}>
          <CategoryChip category={item.category} />
          <PriorityChip priority={item.priority} />
          {item.lastMessagePreview && (
            <span className={styles.root_main_preview}>
              {item.lastMessagePreview}
            </span>
          )}
        </div>
      </div>
      <div className={styles.root_right}>
        <StatusChip status={item.status} />
        <div className={styles.root_right_date}>
          {dateUtility.formatRelativeDate(item.lastMessageAt ?? item.createdOn)}
        </div>
      </div>
    </div>
  );
};

export const StatusChip = ({ status }: { status: SupportChatStatus }) => {
  const config: Record<
    SupportChatStatus,
    { label: string; className: string }
  > = {
    [SupportChatStatus.Open]: { label: "Отворен", className: styles.chip_open },
    [SupportChatStatus.Closed]: {
      label: "Затворен",
      className: styles.chip_closed,
    },
  };
  const { label, className } = config[status];
  return <span className={classNames(styles.chip, className)}>{label}</span>;
};

export const CategoryChip = ({
  category,
}: {
  category: SupportChatCategory;
}) => {
  const colorClass =
    {
      [SupportChatCategory.Technical]: styles.category_chip_technical,
      [SupportChatCategory.FeatureRequest]: styles.category_chip_feature,
      [SupportChatCategory.Bug]: styles.category_chip_bug,
      [SupportChatCategory.Billing]: styles.category_chip_billing,
      [SupportChatCategory.Other]: styles.category_chip_other,
      [SupportChatCategory.General]: styles.category_chip_other,
    }[category] ?? styles.category_chip_other;
  return (
    <span className={classNames(styles.category_chip, colorClass)}>
      {supportChatCategoryLabels[category]}
    </span>
  );
};

export const PriorityChip = ({
  priority,
}: {
  priority: SupportChatPriority;
}) => {
  const className = {
    [SupportChatPriority.Low]: styles.priority_chip_low,
    [SupportChatPriority.Medium]: styles.priority_chip_medium,
    [SupportChatPriority.High]: styles.priority_chip_high,
    [SupportChatPriority.Blocker]: styles.priority_chip_blocker,
  }[priority];
  return (
    <span className={classNames(styles.priority_chip, className)}>
      {supportChatPriorityLabels[priority]}
    </span>
  );
};
